import React, { useMemo, useState } from 'react';
import { SlidersHorizontal } from 'lucide-react';
import { Tag, TagCategoryWithTags } from '../../types';
import { CategoryPopover } from './CategoryPopover';
import { ActiveTagChips } from './ActiveTagChips';
import { MobileFilterSheet } from './MobileFilterSheet';

interface TagFilterBarProps {
  categories: TagCategoryWithTags[];
  selectedTagIds: Set<string>;
  onToggle: (tagId: string) => void;
  onClear?: () => void;
  /** Map of tagId → number of courses with that tag (in the current course set). */
  tagCounts?: Record<string, number>;
  /**
   * Render without the bordered band so the bar can sit inline beside other
   * controls (the grouped catalogue puts it on the same line as the pivot
   * toggle — two stacked control rows left a band of dead space before
   * anything clickable).
   */
  bare?: boolean;
}

/**
 * Editorial filter bar.
 *
 * Desktop (≥ sm): "Filter" anchor + per-category popover triggers.
 * Mobile (< sm):  single "Filter" trigger that opens a bottom sheet.
 *
 * In both cases an active-chips row appears below when any tags are selected.
 * Filter logic (OR-within-category, AND-across-categories) is handled by the
 * parent — this component only emits toggle/clear events.
 */
export const TagFilterBar: React.FC<TagFilterBarProps> = ({
  categories,
  selectedTagIds,
  onToggle,
  onClear,
  tagCounts,
  bare,
}) => {
  const [sheetOpen, setSheetOpen] = useState(false);

  // A tag no course carries is a guaranteed dead end (selecting it can only
  // yield an empty grid), so it doesn't earn a spot in the bar. Selected tags
  // are always kept so an active filter can still be seen and removed.
  const visibleCategories = useMemo(
    () =>
      categories
        .map((c) => ({
          ...c,
          tags: tagCounts
            ? c.tags.filter((t) => (tagCounts[t.id] ?? 0) > 0 || selectedTagIds.has(t.id))
            : c.tags,
        }))
        .filter((c) => c.tags.length > 0),
    [categories, tagCounts, selectedTagIds],
  );

  // Flat lookup of all tags so we can resolve selected IDs to Tag rows
  // for the chip row (in selection order across all categories).
  const selectedTags = useMemo<Tag[]>(() => {
    const all = visibleCategories.flatMap((c) => c.tags);
    return all.filter((t) => selectedTagIds.has(t.id));
  }, [visibleCategories, selectedTagIds]);

  if (visibleCategories.length === 0) return null;

  const activeCount = selectedTagIds.size;

  return (
    <div className={bare ? '' : 'border-y border-gray-100 mb-8'}>
      <div className={bare ? '' : 'py-4'}>
        {/* Trigger row */}
        <div className="flex items-center gap-x-6 gap-y-2 flex-wrap">
          {/* Inline beside the pivot's "Show" label, the two must share one
              typeface — the serif treatment stays for the banded stand-alone
              bar (flat catalogue, public catalogue). */}
          <span
            className={
              bare
                ? 'text-[11px] font-bold uppercase tracking-[0.12em] text-gray-400'
                : 'font-serif text-sm uppercase tracking-[0.18em] text-gray-500'
            }
          >
            Filter
          </span>

          {/* Desktop: per-category popovers */}
          <div className="hidden sm:flex items-center gap-x-5 gap-y-2 flex-wrap">
            {visibleCategories.map((category) => (
              <CategoryPopover
                key={category.id}
                category={category}
                selectedTagIds={selectedTagIds}
                onToggle={onToggle}
                tagCounts={tagCounts}
              />
            ))}
          </div>

          {/* Mobile: single sheet trigger */}
          <button
            type="button"
            onClick={() => setSheetOpen(true)}
            className="sm:hidden inline-flex items-center gap-2 py-1 text-sm font-medium text-gray-700 border-b border-gray-300"
            aria-expanded={sheetOpen}
            aria-haspopup="dialog"
          >
            <SlidersHorizontal className="h-4 w-4" />
            <span>Browse tags</span>
            {activeCount > 0 && (
              <span className="text-gray-400 font-normal tabular-nums">· {activeCount}</span>
            )}
          </button>
        </div>

        {/* Active chips */}
        <ActiveTagChips
          tags={selectedTags}
          onRemove={onToggle}
          onClearAll={onClear}
        />
      </div>

      <MobileFilterSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        categories={visibleCategories}
        selectedTagIds={selectedTagIds}
        onToggle={onToggle}
        onClearAll={() => onClear?.()}
        tagCounts={tagCounts}
      />
    </div>
  );
};
